<script setup lang="ts">
import { useToggle } from '@vueuse/core'
import { nextTick } from 'vue'
import { externalLinks } from '~/routes'

const [mobileNavOpen, toggleMobileNav] = useToggle(false)

function navigationClick(event: MouseEvent, linkId: string, linkLabel: string) {
  window.analytics.track(`${linkId}_clicked`, {
    object: linkId,
    action: 'clicked',
    page_url: window.location.href,
    location: 'top_nav',
    label: linkLabel,
  })

  // Deferring the navigation to allow the analytics event to be sent
  const href = (event.currentTarget as HTMLLinkElement).href
  void nextTick(() => {
    window.location.href = href
  })
}

const homepageURL = 'https://www.sleuth.io'
// Copied from https://github.com/sleuth-io/sleuth-marketing-site/blob/main/packages/gatsby/src/content/menus.json
const headerLinks = [
  {
    label: 'Live Demo',
    href: 'https://app.sleuth.io/sleuth/sleuth/metrics/lead_time/',
  },
  {
    label: 'Marketplace',
    href: externalLinks.public_marketplace,
  },
  {
    label: 'Pricing',
    href: '/pricing/',
  },
  {
    label: 'Customers',
    href: '/resources/real-world-stories',
  },
  {
    label: 'Docs',
    href: 'https://help.sleuth.io',
  },
  {
    label: 'Resources',
    href: '/resources/',
  },
]
</script>

<template>
  <header class="tw-relative tw-z-10 tw-bg-[#164159] tw-py-6 tw-text-sm">
    <div class="tw-mx-auto tw-flex tw-max-w-[84rem]">
      <div class="tw-flex tw-grow">
        <a class="tw-mr-14 tw-flex tw-py-0" href="https://www.sleuth.io">
          <span class="tw-invisible tw-absolute tw-h-0 tw-w-0">Home page</span>
          <svg
            width="160"
            height="40"
            fill="#fff"
            viewBox="0 0 160 40"
            aria-hidden="true"
            class="tw-h-10"
          >
            <path
              d="M65.03 24.311c0 1.563-.552 2.794-1.655 3.693-1.096.9-2.623 1.35-4.583 1.35-1.806 0-3.403-.348-4.792-1.042v-3.409c1.142.521 2.106.888 2.894 1.101.794.213 1.52.32 2.175.32.787 0 1.39-.154 1.806-.462.424-.308.636-.765.636-1.373 0-.34-.092-.64-.277-.9-.186-.268-.46-.524-.822-.769-.355-.245-1.084-.635-2.188-1.172-1.033-.497-1.809-.974-2.326-1.432a5.361 5.361 0 0 1-1.238-1.598c-.309-.607-.463-1.318-.463-2.13 0-1.531.505-2.735 1.516-3.61 1.018-.877 2.423-1.314 4.213-1.314.88 0 1.717.106 2.511.32.803.212 1.64.512 2.512.899l-1.157 2.852c-.903-.378-1.652-.643-2.246-.793a6.986 6.986 0 0 0-1.736-.225c-.679 0-1.2.162-1.562.486-.363.323-.544.745-.544 1.266 0 .324.073.608.22.852.146.237.378.47.694.699.324.22 1.084.623 2.28 1.207 1.582.773 2.666 1.55 3.252 2.332.587.773.88 1.724.88 2.852ZM72.008 29.117V11.812h3.588v14.275h6.863v3.03H72.01ZM99.067 29.117h-9.745V11.812h9.745v3.007H92.91v3.8h5.729v3.006h-5.73v4.462h6.158v3.03ZM120.293 11.812V23.01c0 1.279-.282 2.4-.845 3.362-.556.963-1.362 1.7-2.419 2.213-1.057.513-2.307.77-3.75.77-2.176 0-3.866-.569-5.069-1.705-1.204-1.144-1.806-2.706-1.806-4.687v-11.15h3.576v10.594c0 1.333.263 2.312.788 2.935.524.623 1.392.935 2.604.935 1.172 0 2.021-.311 2.546-.935.532-.631.799-1.617.799-2.959v-10.57h3.576ZM134.875 29.117h-3.588V14.866h-4.595v-3.054h12.778v3.054h-4.595v14.25ZM159.851 29.117h-3.577v-7.469h-6.701v7.469h-3.588V11.812h3.588v6.782h6.701v-6.782h3.577v17.305ZM10.048 23.273A10.458 10.458 0 0 1 9.519 20c0-3.464 1.689-6.527 4.28-8.434L9.246 3.14C3.69 6.694 0 12.915 0 20c0 2.73.549 5.33 1.54 7.702l8.508-4.43ZM20 30.48c1.29 0 2.518-.244 3.659-.67l4.552 8.425A19.915 19.915 0 0 1 20 40c-6.343 0-11.992-2.956-15.656-7.562l7.989-5.309A10.44 10.44 0 0 0 20 30.481Zm0-20.962c-1.29 0-2.518.244-3.66.67L11.79 1.764A19.923 19.923 0 0 1 20 0c7.04 0 13.227 3.641 16.79 9.14l-8.466 4.51C26.41 11.143 23.398 9.52 20 9.52ZM30.48 20c0-1.504-.322-2.931-.893-4.225l8.038-5.232A19.907 19.907 0 0 1 40 20c0 7.085-3.689 13.306-9.246 16.858l-4.552-8.423c2.59-1.908 4.279-4.97 4.279-8.435Zm-8.845-.86a1.684 1.684 0 0 0-2.272-.717l-6.444 3.355A7.297 7.297 0 0 1 12.693 20 7.308 7.308 0 1 1 20 27.308a7.276 7.276 0 0 1-4.977-1.967l6.05-4.021a1.692 1.692 0 0 0 .562-2.18Z"
            />
          </svg>
        </a>
        <button
          type="button"
          aria-label="Toggle Primary navigation"
          class="tw-ml-auto tw-mr-3 tw-inline-block tw-bg-transparent tw-py-2 min-[1183px]:tw-hidden"
          @click="toggleMobileNav()"
        >
          <svg
            v-if="!mobileNavOpen"
            width="24"
            height="16"
            fill="none"
            viewBox="0 0 24 16"
            aria-hidden="true"
            variant="inline"
            class="tw-h-[14px]"
          >
            <path
              stroke="#fff"
              stroke-linecap="square"
              stroke-width="1.5"
              d="M1.5 1.5h21M1.5 8h21M1.5 14.5h21"
            />
          </svg>
          <svg
            v-else
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 16 16"
            aria-hidden="true"
            variant="inline"
            class="tw-h-[14px]"
          >
            <path
              stroke="#fff"
              stroke-linecap="square"
              stroke-width="1.5"
              d="m1 1 14 14M15 1 1 15"
            />
          </svg>
        </button>
        <div
          class="tw-top-[88px] tw-w-full tw-grow tw-bg-[#164159] max-[1182px]:tw-absolute max-[1182px]:tw-h-[calc(100vh-88px)] max-[1182px]:tw-flex-col min-[1183px]:tw-flex"
          :class="{ 'tw-flex': mobileNavOpen, 'tw-hidden': !mobileNavOpen }"
        >
          <nav
            class="tw-flex tw-shrink-0 tw-items-center max-[1182px]:tw-flex-col max-[1182px]:tw-items-start max-[1182px]:tw-px-12 max-[1182px]:tw-py-6"
          >
            <a
              v-for="link in headerLinks"
              :key="link.href"
              :href="
                link.href.startsWith('/')
                  ? `${homepageURL}${link.href}`
                  : link.href
              "
              class="hover:tw-opacity-75 max-[1182px]:tw-p-4"
              :class="{
                'active-link tw-pointer-events-none tw-relative':
                  link.label === 'Marketplace',
              }"
            >{{ link.label }}</a>
          </nav>
          <div
            class="tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-3 max-[1182px]:tw-w-full max-[1182px]:tw-grow max-[1182px]:tw-flex-col max-[1182px]:tw-bg-[#0f2f40] max-[1182px]:tw-px-12 max-[1182px]:tw-py-6"
          >
            <a
              href="https://app.sleuth.io/account/signup/"
              class="tw-rounded-md tw-border tw-border-[#236A91] tw-px-[16px] tw-py-[8px] tw-font-[900] max-[1182px]:tw-w-full max-[1182px]:tw-p-4"
              @click="navigationClick($event, 'signup', 'Start free trial')"
            >Start free trial</a>
            <a
              href="https://www.sleuth.io/demo"
              class="tw-rounded-md tw-bg-[#f2c80b] tw-px-[16px] tw-py-[8px] tw-font-[900] tw-text-[#0d0d0d] hover:tw-bg-[#F5D545] max-[1182px]:tw-w-full max-[1182px]:tw-p-4"
              @click="navigationClick($event, 'demo', 'Talk to us')"
            >Talk to us</a>
            <a
              href="https://app.sleuth.io/accounts/login/"
              class="tw-font-[500] max-[1182px]:tw-w-full max-[1182px]:tw-p-4 min-[1183px]:tw-ml-4"
              @click="navigationClick($event, 'login', 'Login')"
            >Login</a>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
header {
  font-family: 'Satoshi', 'Open Sans', sans-serif !important;
}

a {
  color: white;
  transition: all 0.15s;
  padding: 0.75rem;
  line-height: 1.25;
  text-align: center;
}

.active-link::after {
  background-color: rgb(35, 106, 145);
  bottom: 0px;
  content: '';
  height: 1px;
  left: 0.5rem;
  position: absolute;
  right: 0.5rem;
}
</style>
